import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../supabase/client';
import { authMonitor } from './monitoring';
import { traceAuthFlow } from './debug';
import { authRateLimiter } from './rate-limiter';
import { tokenManager } from './token-manager';
import { logger } from '../utils/logger';

export class AuthManager {
  private session: Session | null = null;
  private loading = true;
  private initialized = false;
  private listeners: Array<(session: Session | null) => void> = [];
  private errorListeners: Array<(error: Error) => void> = [];
  private invalidSessionDetected = false;

  constructor() {
    this.initialize();
  }

  private async initialize() {
    try {
      if (!authRateLimiter.canMakeRequest('sessionRestore')) {
        logger.warn('Rate limit hit during session restore, skipping');
        this.loading = false;
        this.initialized = true;
        this.notifyListeners(null);
        return;
      }

      authMonitor.startTimer('sessionRestore');
      traceAuthFlow('AuthManager.initialize');

      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        authMonitor.endTimer('sessionRestore', false, error);
        logger.error('Failed to restore session', { error });
        
        if (this.isInvalidTokenError(error)) {
          logger.warn('Invalid or expired refresh token, clearing session');
          await supabase.auth.signOut();
          this.session = null;
          this.loading = false;
          this.initialized = true;
          this.notifyListeners(null);
          return;
        }
        
        this.notifyError(error);
        this.loading = false;
        this.initialized = true;
        return;
      }

      this.session = session;
      this.loading = false;
      this.initialized = true;
      
      authMonitor.endTimer('sessionRestore', true);
      this.notifyListeners(session);

      supabase.auth.onAuthStateChange(async (event, session) => {
        traceAuthFlow(`AuthStateChange: ${event}`, { userId: session?.user?.id });
        
        if (event === 'TOKEN_REFRESHED' && !session) {
          logger.warn('Token refresh failed, marking session as invalid');
          this.invalidSessionDetected = true;
          await this.signOut();
          return;
        }
        
        this.handleAuthStateChange(event, session);
      });

    } catch (error) {
      authMonitor.endTimer('sessionRestore', false, error as Error);
      logger.error('Failed to initialize auth:', error);
      this.loading = false;
      this.initialized = true;
      this.notifyError(error as Error);
    }
  }

  private handleAuthStateChange(event: string, session: Session | null) {
    traceAuthFlow(`handleAuthStateChange: ${event}`, { hasSession: !!session });
    
    if (event === 'TOKEN_REFRESHED' && !session) {
      logger.warn('Token refresh failed, marking session as invalid');
      this.invalidSessionDetected = true;
      this.signOut();
      return;
    }
    
    if (this.invalidSessionDetected && event !== 'SIGNED_OUT') {
      logger.warn('Ignoring auth event due to invalid session detected');
      return;
    }
    
    switch (event) {
      case 'SIGNED_IN':
        this.session = session;
        this.invalidSessionDetected = false; // Reset on successful sign in
        logger.info('User signed in', { email: session?.user?.email });
        break;
      case 'SIGNED_OUT':
        this.session = null;
        this.invalidSessionDetected = false; // Reset on sign out
        logger.info('User signed out');
        tokenManager.reset();
        break;
      case 'USER_UPDATED':
        this.session = session;
        logger.info('User updated', { email: session?.user?.email });
        break;
    }
    
    this.notifyListeners(session);
  }


  async signIn(email: string, password: string): Promise<{ session: Session | null; error: Error | null }> {
    try {
      authMonitor.startTimer('signIn');
      traceAuthFlow('AuthManager.signIn', { email });

      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        authMonitor.endTimer('signIn', false, error);
        return { session: null, error };
      }

      authMonitor.endTimer('signIn', true);
      return { session: data.session, error: null };
      
    } catch (error) {
      authMonitor.endTimer('signIn', false, error as Error);
      return { session: null, error: error as Error };
    }
  }

  async signInWithProvider(provider: 'google'): Promise<{ error: Error | null }> {
    try {
      traceAuthFlow('AuthManager.signInWithProvider', { provider });
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async signUp(email: string, password: string, metadata?: Record<string, any>): Promise<{ session: Session | null; error: Error | null }> {
    try {
      traceAuthFlow('AuthManager.signUp', { email });
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata }
      });
      
      if (error) return { session: null, error };
      
      return { session: data.session, error: null };
    } catch (error) {
      return { session: null, error: error as Error };
    }
  }

  async signOut(): Promise<{ error: Error | null }> {
    try {
      this.invalidSessionDetected = true; // Mark as invalid before signing out
      await supabase.auth.signOut();
      this.session = null;
      this.notifyListeners(null);
      return { error: null };
    } catch (error) {
      logger.error('Error signing out:', error);
      // Even if sign out fails, clear the session
      this.session = null;
      this.notifyListeners(null);
      tokenManager.reset();
      return { error: error as Error };
    }
  }

  async refreshSession(): Promise<{ session: Session | null; error: Error | null }> {
    try {
      const session = await tokenManager.refreshToken();
      return { session, error: null };
    } catch (error) {
      logger.error('Session refresh failed', { error });
      return { session: null, error: error as Error };
    }
  }


  getSession(): Session | null {
    return this.session;
  }

  getUser(): SupabaseUser | null {
    return this.session?.user ?? null;
  }

  isAuthenticated(): boolean {
    return !!this.session;
  }

  isLoading(): boolean {
    return this.loading;
  }

  isInitialized(): boolean {
    return this.initialized;
  }


  subscribe(listener: (session: Session | null) => void): () => void {
    this.listeners.push(listener);
    
    if (this.initialized) {
      listener(this.session);
    }
    
    return () => {
      this.listeners.splice(this.listeners.indexOf(listener), 1);
    };
  }

  subscribeToErrors(listener: (error: Error) => void): () => void {
    this.errorListeners.push(listener);
    
    return () => {
      this.errorListeners.splice(this.errorListeners.indexOf(listener), 1);
    };
  }


  private isInvalidTokenError(error: any): boolean {
    return (
      error?.message?.includes('refresh_token_not_found') ||
      error?.message?.includes('invalid refresh token') ||
      error?.message?.includes('Token has expired') ||
      error?.status === 400 ||
      error?.status === 401
    );
  }

  private notifyListeners(session: Session | null) {
    this.listeners.forEach(listener => {
      try {
        listener(session);
      } catch (error) {
        logger.error('Auth listener error:', error);
      }
    });
  }

  private notifyError(error: Error) {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (err) {
        logger.error('Error listener error:', err);
      }
    });
  }
}

export const authManager = new AuthManager();

if (typeof window !== 'undefined') {
  (window as any).authManager = authManager;
} 